import { useState, useEffect, useCallback, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { Plus, Tag, Loader2 } from "lucide-react";

interface CouponRow {
  readonly id: string;
  readonly code: string;
  readonly discountType: string;
  readonly discountValue: number;
  readonly maxUses: number | null;
  readonly usageCount: number;
  readonly expiresAt: string | null;
  readonly isActive: boolean;
}

function formatDiscount(type: string, value: number): string {
  if (type === "percentage") return `${value}%`;
  if (type === "fixed") return `₪${(value / 100).toFixed(2)}`;
  return "משלוח חינם";
}

function getCouponStatus(coupon: CouponRow): { label: string; className: string } {
  if (!coupon.isActive) return { label: "לא פעיל", className: "bg-slate-500/15 text-slate-500 border-slate-500/30" };
  if (coupon.expiresAt && coupon.expiresAt < new Date().toISOString()) return { label: "פג תוקף", className: "bg-destructive/15 text-destructive border-destructive/30" };
  if (coupon.maxUses !== null && coupon.usageCount >= coupon.maxUses) return { label: "מוצה", className: "bg-destructive/15 text-destructive border-destructive/30" };
  return { label: "פעיל", className: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30" };
}

function UsageBar({ count, max }: { readonly count: number; readonly max: number | null }): ReactNode {
  if (max === null) {
    return <span className="text-xs text-muted-foreground">{count} / ∞</span>;
  }
  const pct = max > 0 ? Math.min((count / max) * 100, 100) : 0;
  const barColor = pct >= 90 ? "bg-destructive" : pct >= 60 ? "bg-yellow-500" : "bg-emerald-500";
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{count} / {max}</span>
        <span>{Math.round(pct)}%</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div className={cn("h-full rounded-full transition-all", barColor)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function CouponListPage(): ReactNode {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [coupons, setCoupons] = useState<readonly CouponRow[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/coupons", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const json = (await res.json()) as { success: boolean; data: readonly CouponRow[] };
      if (json.success) setCoupons(json.data);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => { void fetchCoupons(); }, [fetchCoupons]);

  const toggleActive = useCallback(async (id: string, currentActive: boolean) => {
    await fetch(`/api/coupons/${id}`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
      body: JSON.stringify({ isActive: !currentActive }),
    });
    void fetchCoupons();
  }, [accessToken, fetchCoupons]);

  return (
    <div className="p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">קופונים</h1>
          {coupons.length > 0 && (
            <p className="text-sm text-muted-foreground mt-0.5">{coupons.length} קופונים</p>
          )}
        </div>
        <Button size="sm" onClick={() => navigate("/coupons/new")} className="min-h-[38px]">
          <Plus className="h-4 w-4 me-1.5" />
          קופון חדש
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : coupons.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 gap-4">
            <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
              <Tag className="h-8 w-8 text-muted-foreground" />
            </div>
            <div className="text-center">
              <p className="font-medium">אין קופונים עדיין</p>
              <p className="text-sm text-muted-foreground mt-1">צור קופון הנחה ראשון לחנות</p>
            </div>
            <Button onClick={() => navigate("/coupons/new")}>
              <Plus className="h-4 w-4 me-1" />
              צור קופון ראשון
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block rounded-xl border bg-card shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40">
                  <th className="p-3 text-start text-xs font-semibold text-muted-foreground uppercase tracking-wide">קוד</th>
                  <th className="p-3 text-start text-xs font-semibold text-muted-foreground uppercase tracking-wide">הנחה</th>
                  <th className="p-3 text-start text-xs font-semibold text-muted-foreground uppercase tracking-wide w-40">שימושים</th>
                  <th className="p-3 text-start text-xs font-semibold text-muted-foreground uppercase tracking-wide">סטטוס</th>
                  <th className="p-3 text-start text-xs font-semibold text-muted-foreground uppercase tracking-wide">תוקף</th>
                  <th className="p-3 text-end text-xs font-semibold text-muted-foreground uppercase tracking-wide">פעולות</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {coupons.map((cp) => {
                  const status = getCouponStatus(cp);
                  return (
                    <tr
                      key={cp.id}
                      className="hover:bg-muted/30 cursor-pointer transition-colors"
                      onClick={() => navigate(`/coupons/${cp.id}`)}
                    >
                      <td className="p-3">
                        <span className="font-mono font-semibold tracking-wider bg-muted px-2 py-1 rounded text-xs">
                          {cp.code}
                        </span>
                      </td>
                      <td className="p-3 font-semibold">{formatDiscount(cp.discountType, cp.discountValue)}</td>
                      <td className="p-3 w-40">
                        <UsageBar count={cp.usageCount} max={cp.maxUses} />
                      </td>
                      <td className="p-3">
                        <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold", status.className)}>
                          {status.label}
                        </span>
                      </td>
                      <td className="p-3 text-xs text-muted-foreground">
                        {cp.expiresAt ? new Date(cp.expiresAt).toLocaleDateString("he-IL") : "ללא הגבלה"}
                      </td>
                      <td className="p-3 text-end" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant={cp.isActive ? "outline" : "default"}
                          className="min-h-[36px]"
                          onClick={() => void toggleActive(cp.id, cp.isActive)}
                        >
                          {cp.isActive ? "השבת" : "הפעל"}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {coupons.map((cp) => {
              const status = getCouponStatus(cp);
              return (
                <Card
                  key={cp.id}
                  className="cursor-pointer hover:border-primary/40 transition-colors shadow-sm"
                  onClick={() => navigate(`/coupons/${cp.id}`)}
                >
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-semibold tracking-wider bg-muted px-2.5 py-1 rounded text-sm">
                        {cp.code}
                      </span>
                      <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold", status.className)}>
                        {status.label}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold">{formatDiscount(cp.discountType, cp.discountValue)}</span>
                      <span className="text-xs text-muted-foreground">
                        {cp.expiresAt ? `עד ${new Date(cp.expiresAt).toLocaleDateString("he-IL")}` : "ללא הגבלה"}
                      </span>
                    </div>
                    <UsageBar count={cp.usageCount} max={cp.maxUses} />
                    <div className="flex justify-end pt-1" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="sm"
                        variant={cp.isActive ? "outline" : "default"}
                        className="min-h-[44px]"
                        onClick={() => void toggleActive(cp.id, cp.isActive)}
                      >
                        {cp.isActive ? "השבת" : "הפעל"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
